import { useEffect, useRef, useState } from 'react';
import { imageUrl } from '../services/api';
import {
  getPhotoVaultStats,
  hydrateOfflineImages,
  isNativePhotoVault,
  listKnownImageUrls,
  persistProductImages,
} from '../utils/imageCache';

const CACHE_KEY = 'oh_productos_cache_v4';
const AUTO_KEY = 'oh_photo_vault_auto_v1';

function urlsFromProductCache() {
  const urls = [];
  try {
    const all = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    for (const slot of Object.values(all || {})) {
      for (const p of slot?.productos || []) {
        const u = imageUrl(p.imagen);
        if (u) urls.push(u);
      }
    }
  } catch {
    /* ignore */
  }
  return [...new Set(urls)];
}

/**
 * 1) Levanta fotos ya en disco (sirve sin internet).
 * 2) Con internet: descarga y guarda el resto en la tablet.
 */
export default function useOfflineImageHydration({ enabled = true } = {}) {
  const started = useRef(false);
  const [progress, setProgress] = useState(null);
  const [vaultCount, setVaultCount] = useState(() => getPhotoVaultStats().saved);

  useEffect(() => {
    if (!enabled || !isNativePhotoVault() || started.current) return undefined;
    started.current = true;
    let cancelled = false;

    (async () => {
      const urls = [...urlsFromProductCache(), ...listKnownImageUrls()];
      await hydrateOfflineImages(urls, { limit: 1500 });
      if (cancelled) return;
      setVaultCount(getPhotoVaultStats().saved);

      const online = typeof navigator === 'undefined' || navigator.onLine !== false;
      if (!online || !urls.length) return;

      // Evita re-descargar el mismo lote cada 2 minutos.
      try {
        const prev = JSON.parse(sessionStorage.getItem(AUTO_KEY) || 'null');
        if (prev?.at && Date.now() - prev.at < 8 * 60 * 1000 && prev.n >= urls.length * 0.8) {
          return;
        }
      } catch {
        /* ignore */
      }

      setProgress({ done: 0, total: urls.length, saved: 0, failed: 0 });
      const result = await persistProductImages(urls, {
        limit: 900,
        onProgress: (p) => {
          if (!cancelled) setProgress(p);
        },
      });
      if (cancelled) return;
      try {
        sessionStorage.setItem(AUTO_KEY, JSON.stringify({ at: Date.now(), n: result.saved }));
      } catch {
        /* ignore */
      }
      setVaultCount(getPhotoVaultStats().saved);
      // Oculta el banner unos segundos después.
      setTimeout(() => {
        if (!cancelled) setProgress(null);
      }, 3500);
    })();

    const onOnline = () => {
      const urls = urlsFromProductCache();
      if (!urls.length) return;
      setProgress({ done: 0, total: urls.length, saved: 0, failed: 0 });
      persistProductImages(urls, {
        limit: 900,
        onProgress: (p) => setProgress(p),
      }).then(() => {
        setVaultCount(getPhotoVaultStats().saved);
        setTimeout(() => setProgress(null), 3500);
      });
    };
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
    };
  }, [enabled]);

  return { progress, vaultCount };
}
